if (!customElements.get('watch-shop-stories')) {
  customElements.define(
    'watch-shop-stories',
    class WatchShopStories extends HTMLElement {
      connectedCallback() {
        this.stories = [...this.querySelectorAll('[data-watch-story]')];
        this.videos = this.stories.map((story) => story.querySelector('video')).filter(Boolean);
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.desktop = window.matchMedia('(min-width: 1024px)');
        this.rail = this.querySelector('[data-watch-rail]');
        this.previousButton = this.querySelector('[data-watch-previous]');
        this.nextButton = this.querySelector('[data-watch-next]');
        this.ratios = new Map();
        this.autoplay = this.dataset.autoplay === 'true';

        this.videos.forEach((video) => {
          video.muted = true;
          video.defaultMuted = true;
          video.loop = true;
          video.playsInline = true;
          video.controls = false;
        });
        this.videoReadyHandler = () => this.updatePlayback();
        this.videos.forEach((video) => video.addEventListener('canplay', this.videoReadyHandler));

        this.loadObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const video = entry.target.querySelector('video');
            if (video && video.preload === 'none') {
              video.preload = 'metadata';
              video.load();
            }
            this.loadObserver.unobserve(entry.target);
          });
        }, { rootMargin: '400px 0px' });

        this.playObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => this.ratios.set(entry.target, entry.intersectionRatio));
          this.updatePlayback();
        }, { threshold: [0, .25, .5, .75, 1] });

        this.stories.forEach((story) => {
          this.loadObserver.observe(story);
          this.playObserver.observe(story);
        });

        this.visibilityHandler = () => this.updatePlayback();
        this.motionHandler = () => this.updatePlayback();
        this.breakpointHandler = () => this.updatePlayback();
        this.scrollHandler = () => this.updateControls();
        this.previousHandler = () => this.moveBy(-1);
        this.nextHandler = () => this.moveBy(1);
        document.addEventListener('visibilitychange', this.visibilityHandler);
        this.reducedMotion.addEventListener('change', this.motionHandler);
        this.desktop.addEventListener('change', this.breakpointHandler);
        this.rail?.addEventListener('scroll', this.scrollHandler, { passive: true });
        this.previousButton?.addEventListener('click', this.previousHandler);
        this.nextButton?.addEventListener('click', this.nextHandler);
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        if (this.rail) this.resizeObserver.observe(this.rail);
        this.updateControls();
      }

      disconnectedCallback() {
        this.loadObserver?.disconnect();
        this.playObserver?.disconnect();
        document.removeEventListener('visibilitychange', this.visibilityHandler);
        this.reducedMotion?.removeEventListener('change', this.motionHandler);
        this.desktop?.removeEventListener('change', this.breakpointHandler);
        this.rail?.removeEventListener('scroll', this.scrollHandler);
        this.previousButton?.removeEventListener('click', this.previousHandler);
        this.nextButton?.removeEventListener('click', this.nextHandler);
        this.resizeObserver?.disconnect();
        this.videos?.forEach((video) => video.removeEventListener('canplay', this.videoReadyHandler));
      }

      currentStoryIndex() {
        if (!this.rail || !this.stories.length) return 0;
        return this.stories.reduce((closest, story, index) => {
          const distance = Math.abs(story.offsetLeft - this.rail.scrollLeft);
          return distance < closest.distance ? { index, distance } : closest;
        }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
      }

      updateControls() {
        const index = this.currentStoryIndex();
        if (this.previousButton) this.previousButton.disabled = index <= 0;
        if (this.nextButton) this.nextButton.disabled = index >= this.stories.length - 1;
      }

      moveBy(direction) {
        if (!this.rail || !this.stories.length) return;
        const nextIndex = Math.min(this.stories.length - 1, Math.max(0, this.currentStoryIndex() + direction));
        this.rail.scrollTo({
          left: this.stories[nextIndex].offsetLeft,
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      pauseOthers(activeVideo = null) {
        this.videos.forEach((video) => {
          if (video !== activeVideo) video.pause();
        });
      }

      play(video) {
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        const promise = video.play();
        if (promise?.catch) promise.catch(() => {});
      }

      updatePlayback() {
        this.updateControls();
        if (!this.autoplay || this.reducedMotion.matches || document.hidden) {
          this.pauseOthers();
          return;
        }
        if (this.desktop.matches) {
          this.stories.forEach((story) => {
            const video = story.querySelector('video');
            if ((this.ratios.get(story) || 0) >= .25) this.play(video);
            else video?.pause();
          });
          return;
        }

        const visibleStory = [...this.ratios.entries()]
          .filter(([, ratio]) => ratio >= .35)
          .sort((left, right) => right[1] - left[1])[0]?.[0];
        const activeVideo = visibleStory?.querySelector('video');
        this.pauseOthers(activeVideo);
        this.play(activeVideo);
      }

    },
  );
}
